import { useMemo } from 'react';

import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';

import { tagDropdownStyles, tagDropdownWrapperStyles } from './BlogListPage.styles';

interface IBlogTagDropdownProps {
	tags: string[];
	blogTagPath: string;
	selectedTagPath: string;
	onTagChange: (event: SelectChangeEvent<string>) => void;
}

const toSentenceCase = (value: string) => {
	if (!value) {
		return value;
	}

	return `${value.charAt(0).toUpperCase()}${value.slice(1).toLowerCase()}`;
};

export default function BlogTagDropdown({
	tags,
	blogTagPath,
	selectedTagPath,
	onTagChange,
}: Readonly<IBlogTagDropdownProps>) {
	const normalizedTags = useMemo(() => {
		const seenTags = new Set<string>();

		return tags.reduce<Array<{ raw: string; label: string }>>((acc, tag) => {
			const trimmedTag = tag.trim();

			if (!trimmedTag) {
				return acc;
			}

			const normalizedKey = trimmedTag.toLowerCase();
			if (seenTags.has(normalizedKey)) {
				return acc;
			}

			seenTags.add(normalizedKey);
			acc.push({ raw: trimmedTag, label: toSentenceCase(trimmedTag) });
			return acc;
		}, []);
	}, [tags]);

	if (!normalizedTags.length) {
		return null;
	}

	return (
		<FormControl sx={tagDropdownWrapperStyles}>
			<InputLabel id="blogs-tag-select-label">Browse by tag</InputLabel>
			<Select
				labelId="blogs-tag-select-label"
				label="Browse by tag"
				value={selectedTagPath}
				onChange={onTagChange}
				sx={tagDropdownStyles}
				id="blogs-tag-select"
				data-testid="blogs_tag_dropdown"
			>
				{normalizedTags.map(({ raw, label }) => {
					const tagPath = `${blogTagPath}${raw}`;

					return (
						<MenuItem key={raw} value={tagPath} data-testid={`blogs_tag_dropdown_option_${raw}`}>
							{label}
						</MenuItem>
					);
				})}
			</Select>
		</FormControl>
	);
}
